import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import CompanyBrandMark from './CompanyBrandMark';
import candidateAuthService from '../../services/candidateAuthService';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isLoginDropdownOpen, setIsLoginDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const [user, setUser] = useState(null);
  const [candidateUser, setCandidateUser] = useState(candidateAuthService.getCurrentCandidate());
  const [employeeUser, setEmployeeUser] = useState(null);

  const [pendingAiScreenings, setPendingAiScreenings] = useState([]);
  const [isNotificationDropdownOpen, setIsNotificationDropdownOpen] = useState(false);
  const [isBannerDismissed, setIsBannerDismissed] = useState(false);
  const [nowTs, setNowTs] = useState(Date.now());
  const notificationDropdownRef = useRef(null);

  const location = useLocation();
  const navigate = useNavigate();

  const navigationItems = [
    { path: '/', label: 'Home' },
    { path: '/job-marketplace', label: 'Job Marketplace' },
    { path: '/service-portfolio-hub', label: 'Services' },
    { path: '/global-trade-network', label: 'Trade Network' },
    { path: '/success-stories-showcase', label: 'Success Stories' },
  ];

  const fetchHeaderCandidateScreenings = async () => {
    const cand = candidateAuthService.getCurrentCandidate() ||
                 (localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')) : null) ||
                 (localStorage.getItem('candidate_user') ? JSON.parse(localStorage.getItem('candidate_user')) : null);

    console.log('[CandidateAiNotification] Resolving candidate profile:', cand);

    if (cand?.email || cand?.id || candidateAuthService.isAuthenticated()) {
      try {
        console.log('[CandidateAiNotification] Querying getMyScreenings for candidate:', cand?.email || cand?.id);
        const res = await candidateAuthService.getMyScreenings();
        console.log('[CandidateAiNotification] API getMyScreenings response:', res);

        if (res.success && Array.isArray(res.data)) {
          const activeAi = res.data.filter(s => {
            const isCompleted = s.ai_screening_status === 'COMPLETED' || 
                                s.ai_screening_status === 'PASSED' || 
                                s.ai_screening_status === 'FAILED';
            const isExpired = s.is_expired || s.ai_screening_status === 'EXPIRED';
            return !isCompleted && !isExpired;
          });
          console.log('[CandidateAiNotification] Found active pending AI screenings:', activeAi.length, activeAi);
          setPendingAiScreenings(activeAi);
        } else {
          setPendingAiScreenings([]);
        }
      } catch (err) {
        console.error('[CandidateAiNotification] Error fetching candidate screenings:', err);
        setPendingAiScreenings([]);
      }
    } else {
      console.log('[CandidateAiNotification] No candidate identity in session.');
      setPendingAiScreenings([]);
    }
  };

  const getRemainingText = (sc) => {
    let targetTime = null;
    if (sc.ai_screening_deadline) {
      targetTime = new Date(sc.ai_screening_deadline).getTime();
    } else if (typeof sc.remaining_seconds === 'number' && sc.remaining_seconds > 0) {
      if (!sc._deadlineTs) {
        sc._deadlineTs = Date.now() + sc.remaining_seconds * 1000;
      }
      targetTime = sc._deadlineTs;
    } else if (sc.created_at || sc.applied_at) {
      targetTime = new Date(sc.created_at || sc.applied_at).getTime() + 24 * 3600 * 1000;
    }

    if (!targetTime) return '24h remaining';
    const diffSecs = Math.max(0, Math.floor((targetTime - nowTs) / 1000));
    if (diffSecs <= 0) return 'Expired';
    const h = Math.floor(diffSecs / 3600);
    const m = Math.floor((diffSecs % 3600) / 60);
    const s = diffSecs % 60;
    if (h > 0) return `${h}h ${m}m remaining`;
    return `${m}m ${s}s remaining`;
  };

  const checkAuth = () => {
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      try { setUser(JSON.parse(savedUser)); } catch (e) { setUser(null); }
    } else {
      setUser(null);
    }
    const savedEmp = localStorage.getItem('employeeUser');
    if (savedEmp) {
      try { setEmployeeUser(JSON.parse(savedEmp)); } catch (e) { setEmployeeUser(null); }
    } else {
      setEmployeeUser(null);
    }
    setCandidateUser(candidateAuthService.getCurrentCandidate());
  };

  useEffect(() => {
    checkAuth();
    fetchHeaderCandidateScreenings();

    const handleAuthChange = () => {
      checkAuth();
      fetchHeaderCandidateScreenings();
    };

    window.addEventListener('authChange', handleAuthChange);
    window.addEventListener('candidateAuthChange', handleAuthChange);

    const unsub = candidateAuthService.onAuthStateChange(({ user: cUser }) => {
      setCandidateUser(cUser);
      fetchHeaderCandidateScreenings();
    });

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsLoginDropdownOpen(false);
      }
      if (notificationDropdownRef.current && !notificationDropdownRef.current.contains(event.target)) {
        setIsNotificationDropdownOpen(false);
      }
    };

    const ticker = setInterval(() => {
      setNowTs(Date.now());
    }, 1000);

    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      clearInterval(ticker);
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('authChange', handleAuthChange);
      window.removeEventListener('candidateAuthChange', handleAuthChange);
      unsub.unsubscribe();
    };
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('employeeUser');
    checkAuth();
    window.location.href = '/';
  };

  const handleCandidateLogout = async () => {
    await candidateAuthService.logout();
    checkAuth();
  };

  const isActivePath = (path) => {
    if (path === '/') {
      return location?.pathname === '/' || location?.pathname === '/homepage';
    }
    return location?.pathname === path;
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return parts[0].substring(0, 2).toUpperCase();
  };

  const activeUser = employeeUser || candidateUser || user;
  const activePortalName = employeeUser
    ? 'Employee Portal'
    : candidateUser
    ? 'Candidate Portal'
    : user
    ? 'HR & Payroll ERP'
    : '';
  const activeUserName = employeeUser?.full_name || candidateUser?.full_name || candidateUser?.email || user?.name || user?.email || 'User';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-40 bg-[#060D21] border-b border-slate-800/80 transition-all duration-200 ${
        isScrolled ? 'py-2.5 bg-[#060D21]/95 backdrop-blur-sm' : 'py-3.5'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-14 md:h-16">
        
        {/* Logo Mark & Tagline */}
        <Link to="/" className="flex items-center gap-3 flex-shrink-0">
          <CompanyBrandMark
            size="md"
            onDark
            nameClassName="text-xl font-bold text-white font-heading tracking-tight"
            iconWrapperClassName="w-10 h-10 rounded-md flex items-center justify-center text-amber-400 bg-white/10"
          />
        </Link>

        {/* Minimal Restrained Nav (Desktop viewports) */}
        <nav className="hidden lg:flex items-center gap-1 xl:gap-2">
          {navigationItems?.map((item) => (
            <Link
              key={item?.path}
              to={item?.path}
              className={`px-3.5 py-2 rounded-md text-sm font-semibold whitespace-nowrap transition-colors duration-150 ${
                isActivePath(item?.path)
                  ? 'text-amber-400 bg-slate-900/80 border border-slate-800 font-bold'
                  : 'text-slate-300 hover:text-amber-400 hover:bg-slate-900/40'
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        {/* Right-Side Grouping: Active User Badge OR Single Login Dropdown (Visible on all screen sizes) */}
        <div className="flex items-center gap-3">
          {/* Candidate AI Screening Pill Badge (Scoped to Job Marketplace Only) */}
          {location.pathname.startsWith('/job-marketplace') && pendingAiScreenings.length > 0 && (
            <div className="relative" ref={notificationDropdownRef}>
              <button
                type="button"
                onClick={() => setIsNotificationDropdownOpen(!isNotificationDropdownOpen)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-amber-500/20 via-orange-500/20 to-amber-500/20 border border-amber-500/40 text-amber-300 font-bold text-xs hover:bg-amber-500/30 transition-all cursor-pointer shadow-sm group"
                title={`${pendingAiScreenings.length} AI Screening Pending`}
              >
                <span className="w-2 h-2 rounded-full bg-amber-400 animate-ping" />
                <Icon name="Mic" size={13} className="text-amber-400 group-hover:scale-110 transition-transform" />
                <span>{pendingAiScreenings.length} Pending Screening{pendingAiScreenings.length > 1 ? 's' : ''}</span>
                <Icon name="ChevronDown" size={13} className={`text-amber-400 transition-transform duration-150 ${isNotificationDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Notification Dropdown Box */}
              {isNotificationDropdownOpen && (
                <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-[#0B142D] border border-amber-500/40 rounded-xl p-4 z-50 text-white shadow-2xl space-y-3 animate-fade-in">
                  <div className="flex items-center justify-between border-b border-slate-800 pb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-md bg-amber-500/20 text-amber-400 flex items-center justify-center">
                        <Icon name="Mic" size={14} />
                      </div>
                      <span className="text-xs font-bold text-slate-100 uppercase tracking-wider">
                        Pending AI Voice Screenings ({pendingAiScreenings.length})
                      </span>
                    </div>
                    <span className="text-[10px] text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded-full border border-amber-500/20">
                      24h Deadline Active
                    </span>
                  </div>

                  <div className="space-y-2.5 max-h-72 overflow-y-auto pr-1">
                    {pendingAiScreenings.map((sc) => (
                      <div
                        key={sc.id}
                        onClick={() => {
                          setIsNotificationDropdownOpen(false);
                          navigate(`/ai-screening/${sc.id}`);
                        }}
                        className="bg-slate-900/90 border border-slate-800 hover:border-amber-500/50 rounded-lg p-3 cursor-pointer transition-all hover:bg-slate-800/80 space-y-1.5 group"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <h4 className="text-xs font-bold text-slate-100 group-hover:text-amber-400 transition-colors line-clamp-1">
                            {sc.job_title || sc.position || 'Voice Interview'}
                          </h4>
                          <span className="text-[10px] font-bold text-amber-400 bg-amber-400/10 px-2 py-0.5 rounded whitespace-nowrap">
                            {getRemainingText(sc)}
                          </span>
                        </div>
                        <p className="text-[11px] text-slate-400 line-clamp-1">
                          {sc.job_company ? `${sc.job_company}` : 'Employer Requirement'}
                        </p>
                        <div className="flex items-center justify-between text-[11px] text-amber-400 font-semibold pt-1">
                          <span>AI Voice Screening Pending — {getRemainingText(sc)}</span>
                          <span className="inline-flex items-center gap-1 text-xs text-amber-300 group-hover:translate-x-0.5 transition-transform">
                            Resume <Icon name="ArrowRight" size={12} />
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeUser ? (
            <div className="flex items-center gap-2.5 bg-slate-900 border border-slate-800 rounded-md pl-2 pr-3 py-1 text-white">
              <div className="w-7 h-7 rounded-md bg-amber-400/15 border border-amber-400/30 text-amber-400 font-bold text-xs flex items-center justify-center flex-shrink-0">
                {getInitials(activeUserName)}
              </div>
              <div className="flex flex-col text-left leading-tight max-w-[140px]">
                <span className="text-xs font-bold text-slate-100 truncate">{activeUserName}</span>
                <span className="text-[10px] text-amber-400 font-medium truncate">{activePortalName}</span>
              </div>
              <button
                type="button"
                onClick={candidateUser ? handleCandidateLogout : handleLogout}
                className="p-1 text-slate-400 hover:text-red-400 hover:bg-slate-800 rounded transition-colors ml-1"
                title="Logout"
              >
                <Icon name="LogOut" size={14} />
              </button>
            </div>
          ) : (
            <div className="relative" ref={dropdownRef}>
              <button
                type="button"
                onClick={() => setIsLoginDropdownOpen(!isLoginDropdownOpen)}
                className="inline-flex items-center gap-2 px-3.5 sm:px-4 py-2 rounded-md bg-[#060D21] border border-amber-400/60 text-amber-400 hover:bg-amber-400/10 font-bold text-xs sm:text-sm transition-colors duration-150"
                aria-expanded={isLoginDropdownOpen}
                aria-haspopup="true"
              >
                <Icon name="User" size={15} />
                <span>Login</span>
                <Icon
                  name="ChevronDown"
                  size={14}
                  className={`transition-transform duration-150 ${isLoginDropdownOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {/* Flat Enterprise Login Dropdown Menu */}
              {isLoginDropdownOpen && (
                <div className="absolute right-0 top-full mt-2 w-72 sm:w-80 bg-[#0B142D] border border-slate-800 rounded-md p-1.5 z-50 text-white shadow-2xl">
                  {/* Mobile Navigation Section (Shown on smaller screens) */}
                  <div className="lg:hidden border-b border-slate-800 pb-1 mb-1">
                    <div className="px-3 py-1.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                      Navigation Pages
                    </div>
                    {navigationItems.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setIsLoginDropdownOpen(false)}
                        className={`block px-3 py-2 text-xs font-semibold rounded hover:bg-slate-900 transition-colors ${
                          isActivePath(item.path) ? 'text-amber-400 bg-slate-900' : 'text-slate-300'
                        }`}
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>

                  <div className="px-3 py-2 text-[10px] font-bold text-slate-400 uppercase tracking-widest border-b border-slate-800 mb-1">
                    ISPL Portal Access Points
                  </div>

                  {/* Option 1: Candidate Login */}
                  <button
                    type="button"
                    onClick={() => {
                      setIsLoginDropdownOpen(false);
                      navigate('/job-marketplace');
                    }}
                    className="w-full text-left flex items-start gap-3 p-2.5 rounded hover:bg-slate-900 transition-colors group"
                  >
                    <div className="w-8 h-8 rounded bg-slate-900 border border-slate-800 text-amber-400 flex items-center justify-center flex-shrink-0 group-hover:border-amber-400/40">
                      <Icon name="UserCheck" size={16} />
                    </div>
                    <div>
                      <div className="text-xs font-bold text-slate-200 group-hover:text-amber-400 transition-colors">
                        Candidate Login
                      </div>
                      <div className="text-[11px] text-slate-400 leading-snug">
                        Track job applications & AI screening status
                      </div>
                    </div>
                  </button>

                  {/* Option 2: Employee Login */}
                  <button
                    type="button"
                    onClick={() => {
                      setIsLoginDropdownOpen(false);
                      navigate('/employee/login');
                    }}
                    className="w-full text-left flex items-start gap-3 p-2.5 rounded hover:bg-slate-900 transition-colors group"
                  >
                    <div className="w-8 h-8 rounded bg-slate-900 border border-slate-800 text-amber-400 flex items-center justify-center flex-shrink-0 group-hover:border-amber-400/40">
                      <Icon name="Users" size={16} />
                    </div>
                    <div>
                      <div className="text-xs font-bold text-slate-200 group-hover:text-amber-400 transition-colors">
                        Employee Login
                      </div>
                      <div className="text-[11px] text-slate-400 leading-snug">
                        Attendance, leave & employee self-service
                      </div>
                    </div>
                  </button>

                  {/* Option 3: Client Portal */}
                  <button
                    type="button"
                    onClick={() => {
                      setIsLoginDropdownOpen(false);
                      navigate('/client-portal');
                    }}
                    className="w-full text-left flex items-start gap-3 p-2.5 rounded hover:bg-slate-900 transition-colors group"
                  >
                    <div className="w-8 h-8 rounded bg-slate-900 border border-slate-800 text-amber-400 flex items-center justify-center flex-shrink-0 group-hover:border-amber-400/40">
                      <Icon name="ShieldCheck" size={16} />
                    </div>
                    <div>
                      <div className="text-xs font-bold text-slate-200 group-hover:text-amber-400 transition-colors">
                        Client Portal
                      </div>
                      <div className="text-[11px] text-slate-400 leading-snug">
                        Enterprise staffing & requirement management
                      </div>
                    </div>
                  </button>

                  {/* Option 4: HR & Payroll ERP */}
                  <button
                    type="button"
                    onClick={() => {
                      setIsLoginDropdownOpen(false);
                      navigate('/erp/login');
                    }}
                    className="w-full text-left flex items-start gap-3 p-2.5 rounded hover:bg-slate-900 transition-colors group border-t border-slate-800/60 mt-1 pt-2"
                  >
                    <div className="w-8 h-8 rounded bg-slate-900 border border-slate-800 text-amber-400 flex items-center justify-center flex-shrink-0 group-hover:border-amber-400/40">
                      <Icon name="Building2" size={16} />
                    </div>
                    <div>
                      <div className="text-xs font-bold text-slate-200 group-hover:text-amber-400 transition-colors">
                        HR & Payroll ERP Login
                      </div>
                      <div className="text-[11px] text-slate-400 leading-snug">
                        Admin, HR & manager portal access
                      </div>
                    </div>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* PERSISTENT CANDIDATE PENDING AI SCREENING BANNER STRIP (SCOPED TO JOB MARKETPLACE ONLY) */}
      {location.pathname.startsWith('/job-marketplace') && pendingAiScreenings.length > 0 && !isBannerDismissed && (
        <div className="bg-gradient-to-r from-amber-500/20 via-orange-500/20 to-amber-500/20 border-t border-b border-amber-500/40 py-2 px-4 text-white text-xs backdrop-blur-md shadow-lg animate-fade-in">
          <div className="max-w-7xl mx-auto flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-2 font-medium">
              <span className="w-2 h-2 rounded-full bg-amber-400 animate-ping" />
              <span className="font-bold text-amber-300 uppercase tracking-wider text-[11px]">
                AI Voice Screening Pending:
              </span>
              <span className="text-slate-100 font-semibold">
                "{pendingAiScreenings[0].job_title || pendingAiScreenings[0].position || 'Interview'}" at {pendingAiScreenings[0].job_company || 'Employer'}
              </span>
              <span className="bg-amber-500/20 text-amber-300 px-2.5 py-0.5 rounded-full border border-amber-500/30 text-[11px] font-bold">
                {getRemainingText(pendingAiScreenings[0])}
              </span>
            </div>

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => navigate(`/ai-screening/${pendingAiScreenings[0].id}`)}
                className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-slate-950 font-bold px-3 py-1 rounded-md text-xs transition-transform transform hover:scale-105 shadow-md flex items-center gap-1.5 cursor-pointer"
              >
                <Icon name="Mic" size={13} />
                <span>Resume AI Screening</span>
                <Icon name="ArrowRight" size={13} />
              </button>

              <button
                type="button"
                onClick={() => setIsBannerDismissed(true)}
                className="p-1 text-slate-400 hover:text-white transition-colors"
                title="Dismiss Banner (Bell icon remains active in header)"
              >
                <Icon name="X" size={14} />
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
